<template>
  <div
    :class="['rounded-lg border border-gray-200 bg-gray-50', compact ? 'mb-2 p-1.5' : 'mb-4 p-3']"
  >
    <div
      :class="[
        'flex items-center justify-between text-gray-500',
        compact ? 'mb-1 text-[10px]' : 'mb-2 text-xs'
      ]"
    >
      <span
        >{{ clarifications.length }} question{{ clarifications.length > 1 ? "s" : "" }} to
        answer</span
      >
      <span>{{ answeredCount }} of {{ clarifications.length }} answered</span>
    </div>
    <div class="relative">
      <div
        ref="scrollContainer"
        :class="['flex flex-col overflow-y-auto pr-1', compact ? 'gap-2' : 'gap-4']"
        :style="{ maxHeight: `${CLARIFICATION_CONTAINER_MAX_HEIGHT}px` }"
        @scroll="emit('scroll')"
      >
        <div
          v-for="clarification in clarifications"
          :key="clarification.question"
          :class="['flex flex-col', compact ? 'gap-1.5' : 'gap-2']"
          :data-clarification-label="clarification.label || clarification.question"
        >
          <div
            class="text-sm text-gray-800"
            v-html="injectHexColorPreviews(clarification.question)"
          />
          <div :class="['flex flex-wrap', compact ? 'gap-1.5' : 'gap-2']">
            <template v-for="opt in clarification.options" :key="`${opt.label}-${opt.value}`">
              <FontPreviewOption
                v-if="isFontPreview(clarification)"
                :text-value="getTextValue(clarification)"
                :font-family="
                  clarification.type === 'font_selection' ? opt.value || opt.label : effectiveFont
                "
                :color="
                  clarification.type === 'font_color' ? opt.value || opt.label : effectiveColor
                "
                :option-label="opt.label"
                :option-value="opt.value || opt.label"
                :is-selected="
                  !getResponse(clarification.question).isTypingCustom &&
                  getResponse(clarification.question).selected ===
                    getDisplayText(clarification, opt)
                "
                :is-disabled="isDisabled"
                :preview-type="clarification.type === 'font_selection' ? 'font' : 'color'"
                :compact="compact"
                @select="
                  handleOptionSelect(clarification.question, getDisplayText(clarification, opt))
                "
              />
              <div
                v-else
                role="button"
                :tabindex="isDisabled ? -1 : 0"
                :class="[
                  'cursor-pointer border font-medium',
                  compact ? 'rounded-sm px-2.5 py-1 text-xs' : 'rounded-lg px-4 py-2 text-sm',
                  !getResponse(clarification.question).isTypingCustom &&
                  getResponse(clarification.question).selected ===
                    getDisplayText(clarification, opt)
                    ? 'border-blue-500 bg-blue-50 text-blue-700 hover:bg-blue-100'
                    : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50',
                  isDisabled && 'cursor-not-allowed opacity-50'
                ]"
                :data-testid="`pin-clarify-${opt.label}`"
                @click="
                  !isDisabled &&
                  handleOptionSelect(clarification.question, getDisplayText(clarification, opt))
                "
                @keydown.enter.space.prevent="
                  !isDisabled &&
                  handleOptionSelect(clarification.question, getDisplayText(clarification, opt))
                "
              >
                <span v-html="injectHexColorPreviews(getDisplayText(clarification, opt))" />
              </div>
            </template>
          </div>
          <div
            v-if="getResponse(clarification.question).isTypingCustom"
            class="flex items-center gap-2"
          >
            <input
              :ref="el => autoFocusInput(el)"
              type="text"
              :value="getResponse(clarification.question).custom"
              placeholder="Type your answer..."
              style="font-size: 16px"
              class="flex-1 rounded-md border border-gray-300 px-2.5 py-1.5 outline-none focus:border-blue-500 disabled:opacity-50"
              :disabled="isDisabled"
              :data-testid="`pin-clarify-custom-input-${clarification.type}`"
              @input="emit('custom-input-change', clarification.question, $event.target.value)"
              @keydown.enter.prevent="!isDisabled && emit('submit')"
            />
            <button
              type="button"
              class="cursor-pointer border-none bg-transparent text-[13px] text-gray-500 hover:text-gray-700 disabled:opacity-50"
              :disabled="isDisabled"
              :data-testid="`pin-clarify-cancel-custom-${clarification.type}`"
              @click="emit('toggle-custom-input', clarification.question)"
            >
              Cancel
            </button>
          </div>
          <div
            v-else
            role="button"
            :tabindex="isDisabled ? -1 : 0"
            :class="[
              'cursor-pointer self-start border border-dashed border-gray-300 text-blue-600 hover:border-blue-400 hover:bg-blue-50 hover:text-blue-700',
              compact ? 'rounded-sm px-2.5 py-1 text-xs' : 'rounded-lg px-4 py-2 text-sm',
              isDisabled && 'cursor-not-allowed opacity-50'
            ]"
            :data-testid="`pin-clarify-type-own-${clarification.type}`"
            @click="!isDisabled && emit('toggle-custom-input', clarification.question)"
            @keydown.enter.space.prevent="
              !isDisabled && emit('toggle-custom-input', clarification.question)
            "
          >
            + Type my own
          </div>
        </div>
      </div>
      <div
        v-if="unansweredAbove > 0"
        role="button"
        tabindex="0"
        :aria-label="`Scroll to ${unansweredAbove} unanswered above`"
        :class="[
          'absolute left-1/2 top-[-4px] z-10 -translate-x-1/2 cursor-pointer rounded-full bg-blue-500 font-medium text-white shadow-md hover:bg-blue-600',
          compact ? 'px-2 py-0.5 text-[10px]' : 'px-3 py-1 text-xs'
        ]"
        @click="emit('scroll-to-unanswered', 'above')"
        @keydown.enter.space.prevent="emit('scroll-to-unanswered', 'above')"
      >
        &uarr; {{ unansweredAbove }} unanswered above
      </div>
      <div
        v-if="unansweredBelow > 0"
        role="button"
        tabindex="0"
        :aria-label="`Scroll to ${unansweredBelow} unanswered below`"
        :class="[
          'absolute bottom-[-4px] left-1/2 z-10 -translate-x-1/2 cursor-pointer rounded-full bg-blue-500 font-medium text-white shadow-md hover:bg-blue-600',
          compact ? 'px-2 py-0.5 text-[10px]' : 'px-3 py-1 text-xs'
        ]"
        @click="emit('scroll-to-unanswered', 'below')"
        @keydown.enter.space.prevent="emit('scroll-to-unanswered', 'below')"
      >
        {{ unansweredBelow }} unanswered below &darr;
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, nextTick } from "vue";
import FontPreviewOption from "./FontPreviewOption.vue";
import { injectHexColorPreviews } from "./composables/useMarkdownRenderer";

defineOptions({ name: "PinClarifications" });

const CLARIFICATION_CONTAINER_MAX_HEIGHT = 280;
const DEFAULT_RESPONSE = { selected: null, custom: "", isTypingCustom: false };

const props = defineProps({
  clarifications: { type: Array, required: true },
  responses: { type: Object, required: true },
  isDisabled: { type: Boolean, default: false },
  textInfo: { type: Object, default: undefined },
  selectedFont: { type: String, default: undefined },
  selectedColor: { type: String, default: undefined },
  unansweredAbove: { type: Number, default: 0 },
  unansweredBelow: { type: Number, default: 0 },
  compact: { type: Boolean, default: false }
});

const emit = defineEmits([
  "select-option",
  "toggle-custom-input",
  "custom-input-change",
  "submit",
  "scroll",
  "scroll-to-unanswered"
]);

const answeredCount = computed(
  () =>
    props.clarifications.filter(c => {
      const r = props.responses[c.question];
      if (!r) return false;
      if (r.isTypingCustom) return !!r.custom?.trim();
      return !!r.selected;
    }).length
);

const effectiveFont = computed(() => props.selectedFont || props.textInfo?.fontFamily || "inherit");

const effectiveColor = computed(() => props.selectedColor || props.textInfo?.color || "#000000");

function getResponse(question) {
  return props.responses[question] ?? DEFAULT_RESPONSE;
}

function isFontPreview(clarification) {
  return clarification.type === "font_selection" || clarification.type === "font_color";
}

function getTextValue(clarification) {
  return props.textInfo?.value || clarification.label || "Sample";
}

function getDisplayText(clarification, opt) {
  const isFontColor = clarification.type === "font_color";
  const isColorShade = clarification.type === "color_shade";
  return (isColorShade || isFontColor) && opt.value ? `${opt.label} (${opt.value})` : opt.label;
}

let prevCustomInputEl = null;

function autoFocusInput(el) {
  if (el && el !== prevCustomInputEl) {
    prevCustomInputEl = el;
    nextTick(() => el.focus());
  }
  if (!el) {
    prevCustomInputEl = null;
  }
}

function handleOptionSelect(question, label) {
  const response = getResponse(question);
  const isSelected = !response.isTypingCustom && response.selected === label;
  emit("select-option", question, isSelected ? null : label);
}
</script>
